/**
 * Animated build pipeline — port of `fe-prototype/build/Pipeline.jsx`.
 *
 * The animation is driven by:
 *   1. A timed advance through the 6 stages at the prototype's cadence
 *      (so the user always sees a sense of progress even if the API is
 *      quiet),
 *   2. The real build status received via SSE — if the backend reports
 *      `ready` before all stages have animated, we keep the animation
 *      moving but cap it: the parent flips to Review the moment status
 *      becomes terminal.
 *
 * Stage metric chips are data-driven. While the build is in flight we
 * poll `/kb/{slug}`, `/score/{slug}`, and `/eval/{slug}` and swap each
 * stage's chip for a value derived from real backend signals when it
 * becomes available. Stages without a live signal (or whose signal
 * hasn't landed yet) fall back to a generic ``fallbackMetric`` if the
 * stage defines one, or render nothing — never a fabricated number.
 *
 * The final stage stays in ``active`` until the parent unmounts on
 * ``status='ready'`` — the timer no longer marks it done — and after
 * the animation budget elapses we flip the copy to "Almost there —
 * final checks running…" so the page doesn't read as stuck on slow
 * builds.
 */

import { useEffect, useRef, useState } from "react";

import { getEval, getKb, getScore } from "./api.js";
import { Icon } from "./Icon.js";
import { BUILD_STAGES } from "./stages.js";
import type { BusinessProfile, EvalRow, KbScoreRow } from "./types.js";

interface PipelineProps {
  /** Friendly business name shown in the header chip. */
  businessName: string;
  /**
   * Build slug. When present, the pipeline polls `/kb`, `/score`, and
   * `/eval` to swap stage metric chips for real backend values. When
   * absent (legacy callers), the chips stay on their static fallbacks.
   */
  slug?: string | undefined;
}

const _POLL_INTERVAL_MS = 2000;

export function Pipeline({ businessName, slug }: PipelineProps) {
  const [active, setActive] = useState(0);
  const [done, setDone] = useState(-1);
  const [overrun, setOverrun] = useState(false);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [score, setScore] = useState<KbScoreRow | null>(null);
  const [evalRow, setEvalRow] = useState<EvalRow | null>(null);
  const timers = useRef<number[]>([]);
  const lastStageIndex = BUILD_STAGES.length - 1;

  useEffect(() => {
    let t = 400;
    BUILD_STAGES.forEach((s, i) => {
      timers.current.push(window.setTimeout(() => setActive(i), t));
      t += s.ms;
      if (i < lastStageIndex) {
        timers.current.push(window.setTimeout(() => setDone(i), t - 220));
      }
    });
    timers.current.push(window.setTimeout(() => setOverrun(true), t + 600));
    const localTimers = timers.current;
    return () => {
      localTimers.forEach((id) => window.clearTimeout(id));
    };
  }, [lastStageIndex]);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const [kb, scoreRow, evalLatest] = await Promise.all([
          profile === null ? getKb(slug).catch(() => null) : null,
          score === null ? getScore(slug).catch(() => null) : null,
          evalRow?.status === "complete"
            ? null
            : getEval(slug).catch(() => null),
        ]);
        if (cancelled) return;
        if (kb?.profile) setProfile(kb.profile);
        if (scoreRow) setScore(scoreRow);
        if (evalLatest) setEvalRow(evalLatest);
      } catch {
        // best-effort polling — silent on transient failures
      }
    };

    void tick();
    const id = window.setInterval(tick, _POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [slug, profile, score, evalRow]);

  return (
    <div className="fade-up" data-testid="build-pipeline">
      <div className="pipe-head">
        <h2>Building your offsider</h2>
        <p>Have a cuppa, this takes a couple of minutes for a real business.</p>
        <div className="pipe-biz">
          <img src="/img/offsider-monogram.svg" alt="" />
          {businessName}
        </div>
      </div>

      <div className="pipe">
        {BUILD_STAGES.map((s, i) => {
          const state =
            i < done + 1 && i !== active
              ? "done"
              : i === active && done < i
                ? "active"
                : i <= done
                  ? "done"
                  : "pending";
          const isActive = state === "active";
          const isDone = state === "done";
          const classes = ["stage", state, s.isEval ? "is-eval" : ""]
            .filter(Boolean)
            .join(" ");
          const metric = isDone
            ? (metricForStage(s.key, { profile, score, evalRow }) ??
              s.fallbackMetric ??
              null)
            : null;
          const secondary = isDone
            ? secondaryForStage(s.key, { profile, score, evalRow })
            : null;
          return (
            <div key={s.key} className={classes}>
              <div className="stage-node">
                {isDone ? (
                  <Icon name="check" size={22} stroke={2.4} />
                ) : (
                  <Icon name={s.icon} size={20} />
                )}
              </div>
              <div className="stage-body">
                <div className="stage-label">{s.label}</div>
                <div className="stage-sub">{s.sub}</div>
                {(isActive || isDone) && (
                  <>
                    <div className="stage-log">
                      {isActive && (
                        <Icon
                          name="loader"
                          size={14}
                          color="var(--accent)"
                          className="dial dot-spin"
                        />
                      )}
                      <span>
                        {isActive && overrun && i === lastStageIndex
                          ? "Almost there — final checks running…"
                          : s.log}
                      </span>
                    </div>
                    {isDone && metric !== null && (
                      <div className="stage-meta">
                        <span
                          className="metric-chip"
                          title={secondary ?? undefined}
                        >
                          <Icon name="check" size={13} stroke={2.6} />
                          {metric}
                        </span>
                        {secondary && (
                          <span className="metric-secondary">{secondary}</span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="pipe-foot">
        Reading only what's already public, your website, your Google listing,
        your reviews.
      </div>
    </div>
  );
}

export interface MetricSignals {
  profile: BusinessProfile | null;
  score: KbScoreRow | null;
  evalRow: EvalRow | null;
}

/**
 * Derive a per-stage metric chip from real backend signals.
 *
 * Returns ``null`` when no signal is available yet (the caller falls
 * back to the static ``fallbackMetric`` defined on the stage, or omits
 * the chip entirely if no fallback exists). Never fabricates numbers.
 *
 * Stage-by-stage:
 *
 * - ``resolve`` — relies on the static "Website + Google" fallback;
 *   we don't yet have a backend signal that distinguishes whether
 *   Places landed vs the website-only path.
 * - ``crawl`` — no live signal until the backend surfaces a pages
 *   count; renders nothing (no fabricated "14 pages").
 * - ``classify`` — show the actual extracted vertical from the
 *   profile when present.
 * - ``enrich`` — no live signal yet; renders nothing.
 * - ``synthesize`` — real ``{groups} groups · {items} answers`` from
 *   the freshly-written profile.
 * - ``eval`` — only renders when ``status='complete'``: real
 *   ``Passed X of Y`` with the actor/judge battery counts.
 */
export function metricForStage(
  key: string,
  signals: MetricSignals,
): string | null {
  const { profile, score, evalRow } = signals;
  switch (key) {
    case "classify":
      return profile ? prettyVertical(profile.vertical) : null;
    case "synthesize": {
      if (!profile) return null;
      const groups = profile.kb_groups.length;
      const items = profile.kb_groups.reduce((n, g) => n + g.items.length, 0);
      if (groups === 0 && items === 0) return null;
      const groupsLabel = groups === 1 ? "group" : "groups";
      const itemsLabel = items === 1 ? "answer" : "answers";
      return `${groups} ${groupsLabel} · ${items} ${itemsLabel}`;
    }
    case "eval":
      if (evalRow?.status === "complete") {
        const headline = evalRow.passed_with_data ?? 0;
        if (headline > 0) {
          return `Passed ${headline} of ${evalRow.total} with data`;
        }
        return `Passed ${evalRow.passed} of ${evalRow.total}`;
      }
      if (score) {
        return `Coverage ${score.coverage_score}/100`;
      }
      return null;
    default:
      return null;
  }
}

/**
 * Optional secondary line under the metric chip — for now only the eval
 * stage uses this to surface the soft-failure tier (pass_degraded =
 * "gave a callback") so it isn't invisible behind the headline
 * pass_with_data number.
 */
export function secondaryForStage(
  key: string,
  signals: MetricSignals,
): string | null {
  if (key !== "eval") return null;
  const row = signals.evalRow;
  if (!row || row.status !== "complete") return null;
  const callbacks = row.passed_degraded ?? 0;
  if (callbacks <= 0) return null;
  return `${callbacks} gave a callback`;
}

function prettyVertical(v: string): string {
  return v
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}
